import type { Pixel } from '@/data';
import type { ImageDimensions } from '@/formats/inputSourcesParsers/SourceParser';
import type { Image3DPageFormat, Image3DRowFormat, Image3DSyncFormat } from '@/formats/UnitParser';
import {
  rendering,
  type model, Settings, importer,
} from '@coderline/alphatab';
import type { IClient } from './IClient';

type SvgChunk = {
  svg: string,
  width: number,
  height:number
};

export class AlphaTabService {
  public static readonly QuarterTime: number = 960;

  rawScore : Uint8Array;

  client : IClient;

  alphatabSettings : Settings;

  model? : model.Score;

  renderer? : rendering.ScoreRenderer;

  svgChunks : SvgChunk[] = [];

  renderResult? : rendering.RenderFinishedEventArgs;

  constructor(
    rawScore : Uint8Array,
    client : IClient,
    alphatabSettings = new Settings(),
  ) {
    this.rawScore = rawScore;
    this.client = client;
    this.alphatabSettings = alphatabSettings;
  }

  async loadAndRender() : Promise<void> {
    await this.loadModel();
    await this.render();
  }

  async loadModel() : Promise<model.Score> {
    // settings.display.layoutMode = LayoutMode.Horizontal;
    const score: model.Score = importer.ScoreLoader
      .loadScoreFromBytes(this.rawScore, this.alphatabSettings);
      // eslint-disable-next-line no-param-reassign
    score.tracks.forEach((t) => t.staves.forEach((s) => { s.showStandardNotation = false; }));
    this.alphatabSettings.core.engine = 'svg';
    this.model = score;
    return score;
  }

  renderSvgChunks() :void {
    const { renderer } = this;
    if (renderer) {
      renderer.preRender.on(() => {
        this.svgChunks = []; // clear on new rendering
      });
      // since 1.2.3 we need to request
      // rendering of each chunk layed out.
      // you can defer rendering based on your needs.
      renderer.partialLayoutFinished.on((r) => {
        renderer.renderResult(r.id);
      });
      let cumulativeSvgChunksYTranslate = 0;
      renderer.partialRenderFinished.on((r) => {
        this.svgChunks.push({
          svg: AlphaTabService.translateSvgChunk(
            r.renderResult as string,
            0,
            cumulativeSvgChunksYTranslate,
          ), // svg string
          width: r.width,
          height: r.height,
        });
        cumulativeSvgChunksYTranslate += r.height;
      });
    }
  }

  async render() : Promise<string> {
    const renderer = new rendering.ScoreRenderer(this.alphatabSettings);
    this.renderer = renderer;
    renderer.width = 1500;

    // 2. Listen to Events
    this.renderSvgChunks();

    const value = new Promise<rendering.RenderFinishedEventArgs>((resolve) => {
      renderer.renderFinished.on((e) => { resolve(e); });
    });
    renderer.renderScore(this.model!, [0]);
    this.renderResult = await value;
    return this.svgImage;
  }

  get positions() : Image3DSyncFormat {
    const page : Image3DPageFormat = {
      image: 'Genrated by alphaTab',
      rows: [],
    };
    if (this.renderer) {
      if (this.renderer.boundsLookup !== null) {
        const lookup : rendering.BoundsLookup|null = this.renderer.boundsLookup;

        const firstStaffChunkIndex = this.svgChunks.findIndex((chunk) => chunk.svg.includes('height="1.17"'));// this height of staff lines in generated svg (could find better condition)
        let cumulativeSvgChunksY = this.svgChunks
          .slice(0, firstStaffChunkIndex)
          .reduce((sum, current) => sum + current.height, 0);
        lookup.staffSystems.forEach(
          (staffSystem) => {
            const row : Image3DRowFormat = {
              staves: [],
              y: cumulativeSvgChunksY,
              height: staffSystem.realBounds.h,
              'start-x': staffSystem.realBounds.x,
              'end-x': staffSystem.realBounds.x + staffSystem.realBounds.w,
              'onset-x': [],
            };

            row.staves?.push(
              {
                top: cumulativeSvgChunksY,
                bottom: cumulativeSvgChunksY + staffSystem.realBounds.h,
              },
            );
            // TODO multiple staves
            /* staffSystem.bars[0].bars.forEach((bar) => {
              row.staves?.push(
                {
                  top: bar.realBounds.y,
                  bottom: bar.realBounds.y + bar.realBounds.h,
                },
              );
            }); */
            cumulativeSvgChunksY += this.svgChunks[staffSystem.index + firstStaffChunkIndex].height;
            staffSystem.bars.forEach((masterBar) => {
              const bar = masterBar.bars[0];
              bar.beats.forEach(
                (beat) => row['onset-x'].push(
                  {
                    onset: beat.beat.absoluteDisplayStart / AlphaTabService.QuarterTime,
                    x: beat.realBounds.x,
                  },
                ),
              );
            });

            page.rows.push(row);
          },
        );
      }
    }

    return { pages: [page] };
  }

  get dimensions() : ImageDimensions {
    if (this.renderResult) {
      return {
        width: this.renderResult.totalWidth as Pixel,
        height: this.renderResult.totalHeight as Pixel,
      };
    }
    return { width: 0 as Pixel, height: 0 as Pixel };
  }

  get svgImage() : string {
    return this.svgChunks.map((c) => c.svg).join('\n');
  }

  static translateSvgChunk(svgChunk:string, x:number, y:number) : string {
    const firstSpaceIndex : number = svgChunk.indexOf(' ');
    return `${svgChunk.slice(0, firstSpaceIndex)} transform="translate(${x} ${y})"${svgChunk.slice(firstSpaceIndex)}`;
  }
}
